import { create } from 'zustand'

export type ToastType = 'message' | 'call' | 'error'

// OSPROD-269: quick-reply target, bound PER TOAST at creation time by the
// socket handler that saw the message. The toast UI never derives the
// destination from globals (active server, current route, voiceStore) —
// with 3 simultaneous toasts each one must reply to exactly its own
// source chat, even after the user navigated elsewhere. Both transports
// address the destination by id alone (dmApi.send → POST
// /dm-channels/:id/messages authorises by DM membership; messageApi.send
// → POST /messages resolves the server from the channel row), so a reply
// to a room on ANOTHER server than the active one still lands correctly —
// the X-Server-Id header rides along but those routes don't scope by it.
export type ToastReplyTarget =
  | { kind: 'dm'; dmChannelId: string }
  | { kind: 'channel'; channelId: string; serverId: string | null }

export interface ToastItem {
  id: string
  type: ToastType
  title: string
  // OSPROD-152: optional middle line (e.g. the message sender's nickname)
  // rendered between title and body, so the "who" never gets truncated
  // out of a single-line title (the channel-message toast used to cram
  // "[Room]: Sender" into one truncated line → users saw the room + text
  // but not who sent it).
  senderName?: string
  body?: string
  avatar?: string
  dmChannelId?: string
  callerId?: string
  callType?: 'audio' | 'video'
  // OSPROD-88: when set, Accept on a 'call' toast joins the LiveKit
  // room directly (PSTN bridge) instead of emitting dm:call:accept.
  // The room is `dm-call-<callSessionId>`, set up by livekit-sip on
  // dispatch.
  phoneCallRoom?: string
  phoneCallSessionId?: string
  // Raw E.164 of the PSTN caller (phone:call:incoming payload). Accept
  // seeds dmCallStore.phoneCallNumber from it — the phone-leg recorder
  // needs the number, and the toast title is a display label, not E.164.
  phoneCallerNumber?: string
  // Unified phone lobby: a PSTN caller is waiting in a voice channel.
  // Accept = voice:waiting:approve + answer-the-phone join/unmute;
  // Decline = voice:waiting:reject. Resolved/expired entries hide the
  // toast via voice:waiting:resolved.
  phoneWaiting?: { channelId: string; serverId: string | null; waitingId: string }
  /**
   * Optional primary action on a non-call toast. When set, ToastContainer
   * renders an inline button labelled `actionLabel`; clicking it invokes
   * `onAction()` and hides the toast. Used for actionable info toasts
   * (e.g. "hot mic detected → Enable DFN"). 'call' toasts ignore this
   * and use the existing Accept/Decline pair instead.
   */
  actionLabel?: string
  onAction?: () => void
  // OSPROD-269: when set, the toast renders an inline "Reply…" input that
  // sends straight to this target (see ToastReplyTarget above).
  replyTarget?: ToastReplyTarget
  createdAt: number
}

interface ToastState {
  toasts: ToastItem[]
  showToast: (toast: Omit<ToastItem, 'createdAt'>) => void
  hideToast: (id: string) => void
}

// Cap visible toasts to 3 — beyond that the column gets noisy and the
// newest message gets buried under a stack of older ones. Oldest gets
// dropped FIFO when a fourth arrives.
const MAX_VISIBLE_TOASTS = 3

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  showToast: (toast) =>
    set((state) => {
      const exists = state.toasts.find((t) => t.id === toast.id)
      if (exists) return state
      const next = [...state.toasts, { ...toast, createdAt: Date.now() }]
      // Drop oldest non-call toasts first; a ringing call should never
      // be evicted by a flurry of message previews.
      while (next.length > MAX_VISIBLE_TOASTS) {
        const idx = next.findIndex((t) => t.type !== 'call')
        if (idx === -1) break
        next.splice(idx, 1)
      }
      return { toasts: next }
    }),
  hideToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}))
